import { css, html } from 'lit';
import { property, state } from 'lit/decorators.js';

import { BaseElement, customElement } from '../../components/base-element';

import '../../components/buttons/icon-button/icon-button.wc';
import '../../components/badge/badge.wc';

declare global {
  interface Window {
    Produktly?: {
      openChangelog: (options: { id: number }) => void;
      getChangelogUnreadCount?: (options: { id: number }) => Promise<number>;
    };
  }
}

/** Max attempts to read the unread count while Produktly's script loads. */
const MAX_UNREAD_POLL_ATTEMPTS = 10;
/** Delay between unread-count poll attempts, in ms. */
const UNREAD_POLL_INTERVAL_MS = 1000;
/** Delay after opening the changelog before re-syncing the unread count, in ms. */
const UNREAD_RESYNC_DELAY_MS = 1500;

@customElement('ps-universal-nav-changelog')
export class UniversalNavChangelogWC extends BaseElement {
  static styles = css`
    :host {
      display: contents;
    }
  `;

  @property({ type: Number, attribute: 'changelog-id' }) changelogId?: number;

  @state() private unreadCount = 0;

  private pollTimer?: ReturnType<typeof setTimeout>;

  connectedCallback(): void {
    super.connectedCallback();
    this.refreshUnreadCount();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = undefined;
    }
  }

  /**
   * Fetch the number of unread changelog posts from Produktly. Produktly's
   * script loads asynchronously, so this retries until the API is available or
   * the attempt budget is exhausted.
   */
  private async refreshUnreadCount(attempt = 0): Promise<void> {
    if (!this.changelogId) {
      return;
    }

    const count = await window.Produktly?.getChangelogUnreadCount?.({
      id: this.changelogId,
    });

    if (typeof count === 'number') {
      this.unreadCount = count;
    } else if (attempt < MAX_UNREAD_POLL_ATTEMPTS) {
      // Produktly is not loaded yet — retry shortly.
      this.pollTimer = setTimeout(
        () => this.refreshUnreadCount(attempt + 1),
        UNREAD_POLL_INTERVAL_MS
      );
    }
  }

  private handleClick(): void {
    if (!this.changelogId) {
      // eslint-disable-next-line no-console
      console.warn(
        '[ps-universal-nav-changelog] No changelog-id attribute provided.'
      );
      return;
    }
    if (!window.Produktly) {
      // eslint-disable-next-line no-console
      console.warn(
        '[ps-universal-nav-changelog] Produktly is not loaded on this page.'
      );
      return;
    }
    window.Produktly.openChangelog({ id: this.changelogId });
    // Opening the changelog marks posts as read on Produktly's side. Clear the
    // badge optimistically, then re-sync to reflect the real count.
    this.unreadCount = 0;
    this.pollTimer = setTimeout(
      () => this.refreshUnreadCount(),
      UNREAD_RESYNC_DELAY_MS
    );
  }

  render() {
    return html`
      <ps-badge variant="danger" .count=${this.unreadCount}>
        <ps-icon-button
          name="changelog"
          @click=${() => this.handleClick()}
        ></ps-icon-button>
      </ps-badge>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-universal-nav-changelog': UniversalNavChangelogWC;
  }
  enum PSElementTagNameMap {
    'ps-universal-nav-changelog' = 'ps-universal-nav-changelog',
  }
}
